import { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import AuthContext from '../../context/AuthContext';

const Dashboard = () => {
  const [orders, setOrders] = useState([]);
  const [activeTab, setActiveTab] = useState('orders');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const { user } = useContext(AuthContext);
  
  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const token = localStorage.getItem('token');
        
        if (!token) {
          setError('Authentication required');
          setLoading(false);
          return;
        }
        
        const res = await axios.get('/api/orders', {
          headers: {
            'x-auth-token': token
          }
        });
        
        setOrders(res.data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching orders:', err);
        setError('Failed to load orders');
        setLoading(false);
      }
    };
    
    fetchOrders();
  }, []);
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'processing':
        return 'bg-blue-100 text-blue-800';
      case 'shipped':
        return 'bg-purple-100 text-purple-800';
      case 'delivered':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="neumorph p-8 rounded-full">
          <svg className="animate-spin h-8 w-8 text-gray-800" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="text-center py-8">
        <div className="neumorph p-6 rounded-lg inline-block">
          <h3 className="text-xl text-red-600 mb-2">Error</h3>
          <p className="text-gray-700">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="neumorph-btn mt-4 px-4 py-2"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div>
      <h1 className="text-3xl font-bold mb-8">My Dashboard</h1>
      
      {/* User Info Card */}
      <div className="neumorph p-6 rounded-lg mb-8">
        <div className="flex flex-col md:flex-row items-center">
          <div className="neumorph p-4 rounded-full mb-4 md:mb-0 md:mr-6">
            <svg className="h-16 w-16 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
          <div>
            <h2 className="text-2xl font-semibold mb-2">{user?.name}</h2>
            <p className="text-gray-600 mb-1">{user?.email}</p>
            {user?.phone && <p className="text-gray-600">{user.phone}</p>}
          </div>
        </div>
      </div>
      
      {/* Dashboard Tabs */}
      <div className="mb-6">
        <div className="flex border-b">
          <button
            className={`py-2 px-4 font-medium ${
              activeTab === 'orders' 
                ? 'border-b-2 border-blue-500 text-blue-600' 
                : 'text-gray-600 hover:text-gray-900'
            }`}
            onClick={() => setActiveTab('orders')}
          >
            My Orders
          </button>
          <button
            className={`py-2 px-4 font-medium ${
              activeTab === 'profile' 
                ? 'border-b-2 border-blue-500 text-blue-600' 
                : 'text-gray-600 hover:text-gray-900'
            }`}
            onClick={() => setActiveTab('profile')}
          >
            Profile
          </button>
        </div>
      </div>
      
      {/* Orders Tab */}
      {activeTab === 'orders' && (
        <div className="neumorph p-6 rounded-lg">
          <h2 className="text-xl font-semibold mb-4">My Orders</h2>
          
          {orders.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-600 mb-4">You haven't placed any orders yet.</p>
              <Link to="/products" className="neumorph-btn px-4 py-2 inline-block">
                Start Shopping
              </Link>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left border-b">
                    <th className="pb-3 font-semibold">Order ID</th>
                    <th className="pb-3 font-semibold">Date</th>
                    <th className="pb-3 font-semibold">Total</th>
                    <th className="pb-3 font-semibold">Status</th>
                    <th className="pb-3 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map(order => (
                    <tr key={order._id} className="border-b last:border-b-0">
                      <td className="py-4 text-sm">{order._id.substring(0, 8)}...</td>
                      <td className="py-4 text-sm">{new Date(order.createdAt).toLocaleDateString()}</td>
                      <td className="py-4 text-sm">${order.totalAmount.toFixed(2)}</td>
                      <td className="py-4 text-sm">
                        <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(order.status)}`}>
                          {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                        </span>
                      </td>
                      <td className="py-4 text-sm">
                        <Link 
                          to={`/dashboard/orders/${order._id}`} 
                          className="neumorph-btn px-3 py-1 text-sm"
                        >
                          View Details
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      
      {/* Profile Tab */}
      {activeTab === 'profile' && (
        <div className="neumorph p-6 rounded-lg">
          <h2 className="text-xl font-semibold mb-4">Profile Information</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-gray-600 mb-1">Name</p>
              <p className="font-medium">{user?.name}</p>
            </div>
            
            <div>
              <p className="text-gray-600 mb-1">Email</p>
              <p className="font-medium">{user?.email}</p>
            </div>
            
            <div>
              <p className="text-gray-600 mb-1">Phone</p>
              <p className="font-medium">{user?.phone || 'Not provided'}</p>
            </div>
            
            <div>
              <p className="text-gray-600 mb-1">Account Type</p>
              <p className="font-medium capitalize">{user?.role}</p>
            </div>
            
            <div>
              <p className="text-gray-600 mb-1">Authentication Method</p>
              <p className="font-medium capitalize">{user?.authMethod}</p>
            </div>
            
            <div>
              <p className="text-gray-600 mb-1">Member Since</p>
              <p className="font-medium">{new Date(user?.createdAt).toLocaleDateString()}</p>
            </div>
          </div>
          
          <div className="mt-6">
            <button className="neumorph-btn px-4 py-2">
              Edit Profile
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
